import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Button, Chip,
    TextField, MenuItem, Tabs, Tab, InputAdornment, Dialog,
    DialogTitle, DialogContent, DialogActions, IconButton
} from '@mui/material';
import {
    Search as SearchIcon, Download as DownloadIcon, Add as AddIcon,
    Visibility as ViewIcon, Edit as EditIcon, Delete as DeleteIcon,
    Print as PrintIcon, Description as DocIcon,
    ArrowUpward as InwardIcon, ArrowDownward as OutwardIcon
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-toastify';

const Letters = () => {
    const [letters, setLetters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [viewDialog, setViewDialog] = useState(false);
    const [selectedLetter, setSelectedLetter] = useState(null);
    const [formData, setFormData] = useState({ title: '', type: '', recipient: '', content: '', template: '' });

    useEffect(() => { fetchLetters(); }, []);

    const fetchLetters = async () => {
        try {
            const res = await api.get('/letters');
            setLetters(res.data.data || []);
        } catch (error) {
            setLetters([
                { id: 1, refNo: 'LTR/2026/001', title: 'Admission Confirmation', type: 'Outward', template: 'Admission', recipient: 'Parent - Aarav Patil', date: '2026-02-10', status: 'Sent', createdBy: 'Admin' },
                { id: 2, refNo: 'LTR/2026/002', title: 'Fee Concession Request', type: 'Inward', template: '-', sender: 'Parent - Sneha Sharma', date: '2026-02-11', status: 'Received', createdBy: 'Clerk' },
                { id: 3, refNo: 'LTR/2026/003', title: 'Transfer Certificate Request', type: 'Inward', template: '-', sender: 'Parent - Rohan K.', date: '2026-02-12', status: 'In Progress', createdBy: 'Clerk' },
                { id: 4, refNo: 'LTR/2026/004', title: 'NOC for Board Exam', type: 'Outward', template: 'NOC', recipient: 'Board Office', date: '2026-02-13', status: 'Sent', createdBy: 'Principal' },
                { id: 5, refNo: 'LTR/2026/005', title: 'Parent Invitation - Annual Day', type: 'Outward', template: 'Event Invite', recipient: 'All Parents', date: '2026-02-14', status: 'Draft', createdBy: 'Admin' },
                { id: 6, refNo: 'LTR/2026/006', title: 'Government Circular', type: 'Inward', template: '-', sender: 'Education Dept.', date: '2026-02-15', status: 'Received', createdBy: 'Principal' },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const filteredLetters = letters.filter(l => {
        const matchTab = tab === 0 ? true : tab === 1 ? l.type === 'Outward' : l.type === 'Inward';
        const matchSearch = !searchTerm || l.title.toLowerCase().includes(searchTerm.toLowerCase()) || l.refNo.toLowerCase().includes(searchTerm.toLowerCase());
        return matchTab && matchSearch;
    });

    const outwardCount = letters.filter(l => l.type === 'Outward').length;
    const inwardCount = letters.filter(l => l.type === 'Inward').length;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DocIcon sx={{ color: '#1a9e8f', fontSize: 28 }} />
                    <Typography variant="h5" fontWeight={700}>Letters & Correspondence</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={() => setOpenDialog(true)}>Create Letter</Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} size="small">Export</Button>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Total Letters</Typography>
                        <Typography variant="h4" fontWeight={700}>{letters.length}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={4}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <OutwardIcon sx={{ color: '#2196f3', fontSize: 28 }} />
                        <Typography variant="body2" color="text.secondary">Outward</Typography>
                        <Typography variant="h4" fontWeight={700} color="primary.main">{outwardCount}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={4}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <InwardIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                        <Typography variant="body2" color="text.secondary">Inward</Typography>
                        <Typography variant="h4" fontWeight={700} color="success.main">{inwardCount}</Typography>
                    </CardContent></Card>
                </Grid>
            </Grid>

            <Card>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tab label={`All (${letters.length})`} />
                    <Tab label={`Outward (${outwardCount})`} />
                    <Tab label={`Inward (${inwardCount})`} />
                </Tabs>
                <Box sx={{ p: 2 }}>
                    <TextField fullWidth size="small" placeholder="Search by title or reference number..." value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)} sx={{ mb: 2 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Ref No.</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>To/From</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Template</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredLetters.map(l => (
                                    <TableRow key={l.id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{l.refNo}</TableCell>
                                        <TableCell>{l.title}</TableCell>
                                        <TableCell>
                                            <Chip size="small" icon={l.type === 'Outward' ? <OutwardIcon /> : <InwardIcon />}
                                                label={l.type} color={l.type === 'Outward' ? 'primary' : 'success'} variant="outlined" />
                                        </TableCell>
                                        <TableCell>{l.type === 'Outward' ? l.recipient : l.sender}</TableCell>
                                        <TableCell>{l.template || '-'}</TableCell>
                                        <TableCell>{new Date(l.date).toLocaleDateString()}</TableCell>
                                        <TableCell>{l.createdBy}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={l.status}
                                                color={l.status === 'Sent' || l.status === 'Received' ? 'success' : l.status === 'In Progress' ? 'warning' : 'default'} />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <IconButton size="small" onClick={() => { setSelectedLetter(l); setViewDialog(true); }}><ViewIcon fontSize="small" /></IconButton>
                                                <IconButton size="small"><PrintIcon fontSize="small" /></IconButton>
                                                <IconButton size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Card>

            {/* View Letter */}
            <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedLetter?.title}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={6}><Typography variant="body2" color="text.secondary">Ref No.</Typography><Typography fontWeight={600}>{selectedLetter?.refNo}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2" color="text.secondary">Type</Typography><Typography fontWeight={600}>{selectedLetter?.type}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2" color="text.secondary">{selectedLetter?.type === 'Outward' ? 'To' : 'From'}</Typography><Typography fontWeight={600}>{selectedLetter?.type === 'Outward' ? selectedLetter?.recipient : selectedLetter?.sender}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2" color="text.secondary">Date</Typography><Typography fontWeight={600}>{selectedLetter?.date}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2" color="text.secondary">Created By</Typography><Typography fontWeight={600}>{selectedLetter?.createdBy}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2" color="text.secondary">Status</Typography><Chip size="small" label={selectedLetter?.status} color="success" /></Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialog(false)}>Close</Button>
                    <Button variant="outlined" startIcon={<PrintIcon />}>Print</Button>
                </DialogActions>
            </Dialog>

            {/* Create Letter */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create Letter</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField select label="Letter Type" fullWidth value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                            <MenuItem value="Outward">Outward</MenuItem>
                            <MenuItem value="Inward">Inward</MenuItem>
                        </TextField>
                        <TextField select label="Template" fullWidth value={formData.template} onChange={(e) => setFormData({ ...formData, template: e.target.value })}>
                            {['Admission', 'NOC', 'Event Invite', 'Fee Receipt', 'Transfer Certificate', 'Character Certificate', 'Custom'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </TextField>
                        <TextField label="Title" fullWidth value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
                        <TextField label="Recipient / Sender" fullWidth value={formData.recipient} onChange={(e) => setFormData({ ...formData, recipient: e.target.value })} />
                        <TextField label="Content" fullWidth multiline rows={4} value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="outlined" onClick={() => { toast.info('Letter saved as draft'); setOpenDialog(false); }}>Save Draft</Button>
                    <Button variant="contained" onClick={() => { toast.success('Letter created & sent'); setOpenDialog(false); }}>Send</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Letters;
